import {action, computed, observable, React} from '@api'
import {ContextMenu2ChildrenProps} from '@api'
import {C8HierarchyStore,FilterChipStore, BuilderCardFieldStore, BuilderCardStore, GroupByGroupHeroCardsStore, GroupByGroupStore, IContextMenuItems, CatalogContextMenu, CatalogLibrariesStore, AssortmentCardStore, AssortmentHeroCardStore, AssortmentHeroCardsStore, HeroCardSecondLevelStore, AssortmentSwatchStore, AssortmentRootStore} from '@modules'
import {DetailsModalNavToolbarStore} from '@modules/CatalogItemDetails'
import {WhiteboardStore} from '@modules/whiteboard'
import {CatalogStore, VipFlexLayoutTabStore, CatalogRootFlexLayoutStore, FlexLayoutStore, StoresInstance} from '@stores'

type ContextMenuInitiatorStore = BuilderCardStore | GroupByGroupStore | CatalogStore | BuilderCardFieldStore | GroupByGroupHeroCardsStore |
  FilterChipStore | IContextMenuItems | CatalogLibrariesStore | FlexLayoutStore | CatalogRootFlexLayoutStore | DetailsModalNavToolbarStore |
  VipFlexLayoutTabStore | AssortmentCardStore | AssortmentHeroCardStore | AssortmentHeroCardsStore | HeroCardSecondLevelStore | AssortmentSwatchStore
  | AssortmentRootStore | WhiteboardStore



/** See YFTS-7958
 * State for the current context menu (right-click or long touch) menu interaction initiated over some 'contextual item' on the screen such
 * as a product card, a group, a style image within that group, the catalog backdrop, etc
 */
export class ContextMenuStore {
  constructor() {
  }
  /**
   * The HTML Element that was originally clicked on to trigger this particular ContextMenu display
   * Do NOT make observable as we don't want to double render when we handle the right-click
   */
  target: EventTarget & HTMLElement

  /**
   * The contextual store of the item right-clicked upon during THIS particular right click event
   * Should be set in the relevant component's onContextMenu event handler
   */
  get store() {
    return this._store
  }

  @observable.ref private _store : ContextMenuInitiatorStore & {contextMenuItems? : React.ReactNode, stores? : StoresInstance}

  @action onContextMenuClosed = e => {
    this.target = null
    this._store = null
  }
  ignoreThisContextMenuEvent = false
  ctx: ContextMenu2ChildrenProps
  readonly rootMenuAttribute = 'ContextRootMenu'

  get contextType() {
    const {store} = this

    if (store instanceof BuilderCardStore) return 'catalogItemCard'
    if (store instanceof GroupByGroupStore) return 'group'
    if (store instanceof C8HierarchyStore) return 'seasonSelector'

    return 'catalog'
  }

  /**
   * Any extra context from the right-click event - say clicking on a specific menu item within a component that we want to tell the context menu about
   */
  @observable.ref extra: any = {}
  @observable.ref contextMenuProps : { className?: string } = {}
  @observable.ref mousePosition : {clientX, clientY}

  @action onComponentContextMenu = (e: React.MouseEvent, store: ContextMenuInitiatorStore, extra : any = {}, contextMenuProps: any = {}) => {
    // DEBUG && console.debug(`onComponentContextMenu`, {store, extra})
    if (!this.store || this.target != e.target) {
      this.target = e.target as HTMLElement
      const {clientX, clientY} = e
      this.mousePosition = {clientX, clientY}
      this._store = store
      this.extra = extra
      this.contextMenuProps = contextMenuProps
    }
    else {
      _.merge(this.extra, {}, extra)
      // console.debug(`Ignoring onComponentContextMenu due to targets not being identical`, {target: this.target, eTarget: e.target, extra, existingStore: this._store, store, e})
    }
  }

  // Should probably only be true after switching workspaces, navigating to a season, etc
  @observable canGoBack = true

  @computed get isCatalogWhitespace() {
    const {store} = this
    return !store
  }

  @computed get isProductCard() {
    const {store} = this
    return store instanceof BuilderCardStore || store instanceof BuilderCardFieldStore
  }

  @computed get isGroupCard() {
    return this.store instanceof GroupByGroupStore
  }

  @computed get isGroupWhitespace() {
    return this.isGroupCard
  }

  get isShowing() { return this.ctx != null}

  onContextMenu = (e: React.MouseEvent<HTMLElement>, ctx: ContextMenu2ChildrenProps) => {
    // 2nd right click should open chrome default menu - Only show default if right-clicking WITHIN the context menu
    if ((e.target as HTMLElement).closest(`.bp3-menu`)) return

    this.ctx = ctx
    if (this.ignoreThisContextMenuEvent) {
      this.ignoreThisContextMenuEvent = false
      return
    }

    if (this.target != e.target) {
      this.target = e.target as HTMLElement
      this._store = null
    }

    const {extra, store} = this
    DEBUG && console.debug(`[Context Menu] - Start`, {extra, store})
    /* Blueprint callback */
    ctx.onContextMenu(e)
  }

  hideContextMenu_DOM = () => {
    const portal = document.querySelector(`[data-type='${this.rootMenuAttribute}']`)?.closest<HTMLDivElement>('.bp3-portal')
    if (portal) {
      portal.style.opacity = '0'
      portal.style.pointerEvents = 'none'
    }
  }

  @action onClosed = () => {
    this.ctx = null
    this.mousePosition = null
  }
}

export const contextMenuStore = new ContextMenuStore()


// WEBPACK FOOTER //
// ./src/modules/contextMenus/ContextMenuStore.ts